import { useEffect } from 'react';
import { useReactFlow, useStore as useRFStore } from '@xyflow/react';
import type { ReactFlowState } from '@xyflow/react';

/**
 * Pure handler factory — exported so unit tests can exercise it without
 * a React context.
 *
 * Intercepts wheel events where horizontal scroll dominates (|deltaX| >
 * |deltaY|) and translates them into a viewport pan, then stops propagation
 * so d3-zoom never sees the event (preventing a simultaneous zoom).
 *
 * Must be attached as a native DOM listener with `{ capture: true, passive:
 * false }` so it fires before d3's own wheel handler on the same element.
 */
export function buildWheelHandler(
  getViewport: () => { x: number; y: number; zoom: number },
  setViewport: (v: { x: number; y: number; zoom: number }, opts?: { duration: number }) => void,
) {
  return function handleWheel(event: WheelEvent) {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    event.preventDefault();
    event.stopPropagation();
    const vp = getViewport();
    setViewport({ x: vp.x - event.deltaX, y: vp.y, zoom: vp.zoom }, { duration: 0 });
  };
}

/**
 * Attaches a native wheel listener (capture phase, non-passive) to the
 * ReactFlow DOM node so horizontal scroll pans the canvas before d3-zoom
 * can interpret it as a zoom gesture.
 *
 * Vertical scroll continues to zoom as normal.
 */
export function useHorizontalScrollPan() {
  const { getViewport, setViewport } = useReactFlow();
  const domNode = useRFStore((s: ReactFlowState) => s.domNode);

  useEffect(() => {
    if (!domNode) return;
    const handler = buildWheelHandler(getViewport, setViewport);
    domNode.addEventListener('wheel', handler, { capture: true, passive: false });
    return () => domNode.removeEventListener('wheel', handler, { capture: true });
  }, [domNode, getViewport, setViewport]);
}
